import { useState } from 'react'
import { usePage } from '@inertiajs/react'
import { MainLayout } from '@/components/layouts'
import { FeaturedArticles, LastContents } from '@/components/home'
import { EditableContent } from '@/components/EditableContent'
import { OnboardingTour } from '@/components/onboarding/OnboardingTour'
import { OnboardingChecklist } from '@/components/onboarding/OnboardingChecklist'
import { BadgeUnlockModal } from '@/components/BadgeUnlockModal'
import { useOnboarding } from '@/hooks/useOnboarding'
import type { Post, HomeArticle, EditableContents, OnboardingStatus, NewlyAwardedBadge, PageProps } from '@/types'

interface SharedProps extends PageProps {
  newly_awarded_badges?: NewlyAwardedBadge[] | null
}

interface HomeProps {
  featured_articles: HomeArticle[]
  recent_posts: Post[]
  editable_contents: EditableContents
  onboarding: OnboardingStatus | null
}

export default function Home({ featured_articles, recent_posts, editable_contents, onboarding }: HomeProps) {
  // Check if there are badges to show (from login/registration)
  const { newly_awarded_badges, community } = usePage<SharedProps>().props
  const hasBadgesToShow = Boolean(newly_awarded_badges && newly_awarded_badges.length > 0)

  const {
    status,
    isTourRunning,
    isTourDismissed,
    startTour,
    completeTour,
    dismissTour,
    restartTour,
  } = useOnboarding(onboarding, { hasBadgesToShow })

  // State for badge unlock animation
  const [awardedBadge, setAwardedBadge] = useState<NewlyAwardedBadge | null>(null)

  const handleTourComplete = async () => {
    const response = await completeTour()
    // Show badge animation if a badge was awarded
    if (response?.newly_awarded_badge) {
      setAwardedBadge(response.newly_awarded_badge)
    }
  }

  const handleCloseBadgeModal = () => {
    setAwardedBadge(null)
  }

  return (
    <MainLayout
      title="Accueil"
      // Pas de banner dans le layout (remplacé par l'onboarding)
    >
      {/* Onboarding Tour */}
      {status && (
        <OnboardingTour
          isRunning={isTourRunning}
          onComplete={handleTourComplete}
          onSkip={dismissTour}
          communitySlug={community?.slug}
        />
      )}

      {/* Banner éditable (si existe) */}
      {editable_contents.banner && (
        <EditableContent
          content={editable_contents.banner}
          wrapperClassName="mb-8 -mt-8 -mx-4"
        />
      )}

      {/* Onboarding Checklist pour les pharmaciens */}
      {status && !status.is_completed && (
        <div className="mb-8">
          <OnboardingChecklist
            status={status}
            onStartTour={startTour}
            onRestartTour={restartTour}
            isTourDismissed={isTourDismissed}
            communitySlug={community?.slug}
          />
        </div>
      )}

      {/* Section Contenus à la une */}
      {featured_articles.length > 0 && (
        <div className="mb-10 space-y-6">
          <h2 className="text-2xl font-heading font-semibold text-text-dark">
            Contenus à la une
          </h2>
          <FeaturedArticles articles={featured_articles} />
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Colonne principale - Dernières publications */}
        <div className="lg:col-span-2 space-y-6">
          <h2 className="text-2xl font-heading font-semibold text-text-dark">
            Dernières publications
          </h2>
          <LastContents posts={recent_posts} />
        </div>

        {/* Sidebar - Événements (réservé aux admins) */}
        {/* mt-14 pour aligner avec la première card (compense le h2 + space-y-6) */}
        <div className="space-y-6 lg:mt-14">
          <EditableContent
            content={editable_contents.events}
            wrapperClassName="bg-white rounded-xl shadow-sm border border-border-light p-6"
          />
        </div>
      </div>

      {/* Badge unlock animation */}
      {awardedBadge && (
        <BadgeUnlockModal
          badge={awardedBadge}
          onClose={handleCloseBadgeModal}
        />
      )}
    </MainLayout>
  )
}
